import type React from 'react';

import { ClearRounded } from '@mui/icons-material';
import { IconButton, InputAdornment, List as MuiList } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import {
  getRecordFallbackReferences,
  OrdBulkUpdateFormButton,
  OrdDualListInput,
  OrdTabbedForm,
  OrdFormList,
  LabelsInput,
  useCanShowLabels,
  useFormType,
  OrdIconStandard,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { GroupUpdateFields, RoleUpdateFields, UserReadFields, dataSchema } from '@ragu-components/ord-types';
import { canAccessWithPermissions } from '@ratto-components/ra-rbac';
import { NumberInput, TabbedForm, usePermissions, useRecordContext, useResourceContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { OrdDescriptionInput } from 'src/components/formInputs';

import { AgentQueuesList } from '../agents/AgentQueuesList';
import { AclResourcesInput } from '../users/AclResourcesInput';

import type { OrdTabbedFormProps } from '@ragu-components/ord-components';
import type { GroupRead } from '@ragu-components/ord-types';
import type { RolePermissions } from 'src/lib/roles';

const ClearNumberAdornment = ({ source }: { source: string }): React.ReactElement | null => {
  const { setValue, watch } = useFormContext();
  const value = watch(source) as number | null | undefined;
  if (value == null) return null;
  return (
    <InputAdornment position='end'>
      <IconButton
        size='small'
        edge='end'
        onClick={() => {
          setValue(source, null);
        }}
      >
        <ClearRounded fontSize='small' />
      </IconButton>
    </InputAdornment>
  );
};

interface RolesPermissionsTabProps extends RolePermissions {
  aclLabelsSource: string;
  aclResourcesSource: string;
}

const RolesPermissionsTab = ({
  aclLabels,
  aclResources,
  aclLabelsSource,
  aclResourcesSource,
}: RolesPermissionsTabProps): React.ReactElement => {
  return (
    <MuiList sx={{ width: '100%' }}>
      <OrdFormList icon={<OrdIconStandard name='labels' />}>
        <LabelsInput
          defaultValue={aclLabels}
          source={aclLabelsSource}
          helperText='resources.groups.helperTexts.acl_labels'
        />
        <AclResourcesInput defaultValue={aclResources} source={aclResourcesSource} />
      </OrdFormList>
    </MuiList>
  );
};

interface State {
  permissions: RolePermissions;
  name: string;
  description: string;
}

interface GroupsFormProps extends OrdTabbedFormProps {}

export const GroupsForm = (props: GroupsFormProps): React.ReactElement => {
  const { create } = useFormType();
  const record = useRecordContext<GroupRead>();
  const resource = useResourceContext() ?? Resources.groups;
  const isRole = resource === Resources.roles;
  const fields = isRole ? RoleUpdateFields : GroupUpdateFields;
  const location = useLocation();
  const state: State = (location.state as State | undefined) ?? {
    name: '',
    description: '',
    permissions: { aclLabels: [], aclResources: [] },
  };
  const canShowLabels = useCanShowLabels();
  const { permissions } = usePermissions();
  const hasUsersListAccess = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.users,
  });
  // Show the tab when access is missing but the record already carries users (readonly fallback)
  const canShowUsers = hasUsersListAccess || getRecordFallbackReferences(record, fields.users).length > 0;
  const canShowAgentQueues = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.agents,
  });
  const { name, description, permissions: rolePermissions } = state;
  const { aclLabels, aclResources } = rolePermissions ?? { aclLabels: [], aclResources: [] };

  const createJsonSchema = (() => {
    if (!isRole) return dataSchema.GroupCreate;

    return {
      ...dataSchema.RoleCreate,
      anyOf: [
        {
          properties: {
            [RoleUpdateFields.acl_labels]: {
              type: 'array',
              minItems: 1,
            },
          },
          required: [RoleUpdateFields.acl_labels],
        },
        {
          properties: {
            [RoleUpdateFields.acl_resources]: {
              type: 'array',
              minItems: 1,
            },
          },
          required: [RoleUpdateFields.acl_resources],
        },
      ],
    };
  })();

  const editJsonSchema = (() => {
    if (!isRole) return dataSchema.GroupUpdate;

    return {
      ...dataSchema.RoleUpdate,
      anyOf: [
        {
          properties: {
            [RoleUpdateFields.acl_labels]: {
              type: 'array',
              minItems: 1,
            },
          },
          required: [RoleUpdateFields.acl_labels],
        },
        {
          properties: {
            [RoleUpdateFields.acl_resources]: {
              type: 'array',
              minItems: 1,
            },
          },
          required: [RoleUpdateFields.acl_resources],
        },
      ],
    };
  })();

  return (
    <OrdTabbedForm
      sanitizeFields
      replaceRelationships
      createJsonSchema={createJsonSchema}
      editJsonSchema={editJsonSchema}
      saveProps={{ alwaysEnable: state !== undefined }}
      deleteProps={create ? undefined : { resource }}
      {...props}
    >
      <TabbedForm.Tab label={`resources.${resource}.operations.sections.groupsInfo`}>
        <MuiList sx={{ width: '100%' }}>
          <OrdFormList icon={<OrdIconStandard name={isRole ? 'roles' : 'groups'} />}>
            <JsonSchemaInput source={fields.name} defaultValue={name} />
            <OrdDescriptionInput source={fields.description} defaultValue={description} />
          </OrdFormList>
          {!isRole && (
            <OrdFormList>
              <NumberInput
                source={GroupUpdateFields.number}
                helperText='resources.groups.helperTexts.number'
                slotProps={{ input: { endAdornment: <ClearNumberAdornment source={GroupUpdateFields.number} /> } }}
              />
            </OrdFormList>
          )}
        </MuiList>
      </TabbedForm.Tab>
      {canShowUsers && (
        <TabbedForm.Tab label='resources.users.name'>
          <OrdDualListInput
            optionText={UserReadFields.username}
            resource={Resources.users}
            source={fields.users}
            sx={{ alignItems: 'center' }}
          />
        </TabbedForm.Tab>
      )}
      {!isRole && !create && canShowAgentQueues && record !== undefined && (
        // height: 100% fills the card, since the tab panel is content-sized by default
        <TabbedForm.Tab label='resources.queues.name' sx={{ height: '100%' }}>
          <AgentQueuesList filterField='group.id' filterValue={record.id} />
        </TabbedForm.Tab>
      )}
      {canShowLabels && isRole && (
        <TabbedForm.Tab label='resources.groups.operations.sections.permissions'>
          <RolesPermissionsTab
            aclLabels={aclLabels}
            aclResources={aclResources}
            aclLabelsSource={fields.acl_labels}
            aclResourcesSource={fields.acl_resources}
          />
        </TabbedForm.Tab>
      )}
    </OrdTabbedForm>
  );
};

/** Form for bulk editing groups */
export const GroupsBulkEditForm = (): React.ReactElement => {
  const inputs = [
    {
      label: 'resources.groups.operations.sections.groupsInfo',
      element: (
        <OrdDualListInput
          optionText={UserReadFields.username}
          resource={Resources.users}
          source={GroupUpdateFields.users}
        />
      ),
    },
  ];
  return <OrdBulkUpdateFormButton inputs={inputs} />;
};
